/**
 * The `/settings` privacy card.
 *
 * The server owns both flag values; `pending` only echoes an in-flight
 * change so a control does not visibly snap back while the mutation is in
 * the air. `isDiscoverable` is a boolean on the profile model, so this
 * renders a two-state control and not a select (see the issue's plan,
 * decision D-1). Success is announced by `SavedNote`, never a toast.
 */
import {
  useBlockList,
  useProfileGet,
  useProfileUpdate,
} from "@sportsmet/profile-sdk/private";
import { Card, Checkbox, SavedNote } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import {
  BLOCKED_LIST_LABEL,
  BLOCKED_LIST_PAGE_LIMIT,
  blockedCountLabel,
  DISCOVERABLE_DESCRIPTION,
  DISCOVERABLE_LABEL,
  POOL_LIST_DESCRIPTION,
  POOL_LIST_LABEL,
  PRIVACY_CARD_TITLE,
  PRIVACY_CONTEXT_MESSAGES,
  PRIVACY_CONTEXT_POOLS,
} from "./privacyScreenLogic";

type typePrivacyField = "isDiscoverable" | "isPoolListVisible";

export const PrivacyCard = (): ReactNode => {
  const profileQuery = useProfileGet({});
  const blockQuery = useBlockList({ limit: BLOCKED_LIST_PAGE_LIMIT });
  const update = useProfileUpdate();

  const [savedField, setSavedField] = useState<typePrivacyField | null>(null);
  const [pending, setPending] = useState<{
    isDiscoverable: boolean | null;
    isPoolListVisible: boolean | null;
  }>({ isDiscoverable: null, isPoolListVisible: null });

  const profile = profileQuery.data?.data?.profile ?? null;

  useEffect(() => {
    const code = extractErrorCode(profileQuery.data);
    if (code !== null)
      showApiError(profileQuery.data, undefined, profileQuery.dataUpdatedAt);
  }, [profileQuery.dataUpdatedAt]);

  useEffect(() => {
    const code = extractErrorCode(blockQuery.data);
    if (code !== null)
      showApiError(blockQuery.data, undefined, blockQuery.dataUpdatedAt);
  }, [blockQuery.dataUpdatedAt]);

  async function handleChange(
    field: typePrivacyField,
    next: boolean,
  ): Promise<void> {
    setPending((current) => ({ ...current, [field]: next }));
    setSavedField(null);
    try {
      const result = await update.mutateAsync({ [field]: next });
      if (!showApiError(result)) setSavedField(field);
      setPending((current) => ({ ...current, [field]: null }));
    } catch {
      showOfflineError();
      setPending((current) => ({ ...current, [field]: null }));
    }
  }

  const discoverableChecked =
    pending.isDiscoverable ?? profile?.isDiscoverable ?? true;
  const poolListChecked =
    pending.isPoolListVisible ?? profile?.isPoolListVisible ?? true;
  const blockedCount = blockQuery.data?.data?.items.length ?? 0;
  const isRosterComplete = (blockQuery.data?.data?.nextCursor ?? null) === null;

  return (
    <Card title={PRIVACY_CARD_TITLE} data-testid="privacy-card">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1" data-testid="privacy-discoverable">
          <Checkbox
            label={DISCOVERABLE_LABEL}
            description={DISCOVERABLE_DESCRIPTION}
            checked={discoverableChecked}
            disabled={profileQuery.isPending}
            onCheckedChange={(next) => {
              void handleChange("isDiscoverable", next === true);
            }}
          />
          <div data-testid="privacy-discoverable-saved">
            <SavedNote saved={savedField === "isDiscoverable"} />
          </div>
        </div>
        <div className="flex flex-col gap-1" data-testid="privacy-pool-list">
          <Checkbox
            label={POOL_LIST_LABEL}
            description={POOL_LIST_DESCRIPTION}
            checked={poolListChecked}
            disabled={profileQuery.isPending}
            onCheckedChange={(next) => {
              void handleChange("isPoolListVisible", next === true);
            }}
          />
          <div data-testid="privacy-pool-list-saved">
            <SavedNote saved={savedField === "isPoolListVisible"} />
          </div>
        </div>
        <Link
          to="/settings/blocked"
          data-testid="privacy-blocked-link"
          className="flex items-center justify-between gap-2 py-3 text-body text-primary underline underline-offset-4"
        >
          <span>{BLOCKED_LIST_LABEL}</span>
          <span
            data-testid="privacy-blocked-count"
            className="tabular-nums text-ink-secondary"
          >
            {blockedCountLabel({
              count: blockedCount,
              isComplete: isRosterComplete,
            })}
          </span>
        </Link>
        <p
          data-testid="privacy-context-pools"
          className="text-caption text-ink-secondary"
        >
          {PRIVACY_CONTEXT_POOLS}
        </p>
        <p
          data-testid="privacy-context-messages"
          className="text-caption text-ink-secondary"
        >
          {PRIVACY_CONTEXT_MESSAGES}
        </p>
      </div>
    </Card>
  );
};
